import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarConferencias, deletarConferencia, obterEstatisticas } from '../../services/conferencia';
import { listarClientes } from '../../services/clientes';
import type { Conferencia, StatusConferencia, ConferenciaEstatisticas } from '../../types/conferencia';
import { getHojeISO, toInicioDiaISO, toFimDiaISO } from '../../utils/dateHelpers';
import './Conferencia.css';

const STATUS_LABELS: Record<StatusConferencia, string> = {
  pendente: 'Pendente',
  em_andamento: 'Em Andamento',
  parcial: 'Parcial',
  concluida: 'Concluída',
  divergente: 'Divergente',
  cancelada: 'Cancelada'
};

const ConferenciaList: React.FC = () => {
  const navigate = useNavigate();
  const [conferencias, setConferencias] = useState<Conferencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [filtroCliente, setFiltroCliente] = useState<string>('');
  const [filtroDataInicio, setFiltroDataInicio] = useState<string>(getHojeISO());
  const [filtroDataFim, setFiltroDataFim] = useState<string>(getHojeISO());
  const [filtroNumeroPedido, setFiltroNumeroPedido] = useState('');
  const [busca, setBusca] = useState('');
  const [estatisticas, setEstatisticas] = useState<ConferenciaEstatisticas | null>(null);
  const [clientesOptions, setClientesOptions] = useState<{ id: number; razao_social: string }[]>([]);

  const carregarConferencias = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, limit: 10 };
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroStatus) params.status = filtroStatus;
      if (filtroCliente) params.cliente_id = filtroCliente;
      if (busca) params.busca = busca;
      const numeroPedidoTrim = filtroNumeroPedido.trim();
      if (numeroPedidoTrim) params.numero_pedido = numeroPedidoTrim;
      // Busca por identificador (numero pedido ou busca) desconsidera data
      // e pesquisa todo historico.
      const buscaAtiva = Boolean((busca && busca.trim()) || numeroPedidoTrim);
      const dataInicioParam = buscaAtiva ? undefined : toInicioDiaISO(filtroDataInicio);
      const dataFimParam = buscaAtiva ? undefined : toFimDiaISO(filtroDataFim);
      if (dataInicioParam) params.dataInicio = dataInicioParam;
      if (dataFimParam) params.dataFim = dataFimParam;

      const result = await listarConferencias(params);
      if (result.success) {
        setConferencias(result.conferencias);
        setTotalPages(result.pagination.totalPages);
      }
    } catch (error) {
      console.error('Erro ao carregar conferências:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filtroTipo, filtroStatus, filtroCliente, busca, filtroDataInicio, filtroDataFim, filtroNumeroPedido]);

  useEffect(() => {
    carregarConferencias();
  }, [carregarConferencias]);

  useEffect(() => {
    const carregarEstatisticas = async () => {
      try {
        const buscaAtiva = Boolean((busca && busca.trim()) || filtroNumeroPedido.trim());
        const result = await obterEstatisticas('mes', {
          dataInicio: buscaAtiva ? undefined : toInicioDiaISO(filtroDataInicio),
          dataFim: buscaAtiva ? undefined : toFimDiaISO(filtroDataFim)
        });
        if (result.success) {
          setEstatisticas(result.estatisticas);
        }
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error);
      }
    };
    carregarEstatisticas();
  }, [filtroDataInicio, filtroDataFim, busca, filtroNumeroPedido]);

  useEffect(() => {
    listarClientes({ limit: 200, ativo: 'true' })
      .then(res => {
        if (res.success && res.clientes) {
          setClientesOptions(res.clientes.map(c => ({ id: c.id, razao_social: c.razao_social })));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          <i className="fas fa-box-open" style={{ marginRight: 8 }}></i>
          Conferência de Carga
        </h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            className="btn btn-success"
            onClick={() => navigate('/conferencia/produtividade')}
          >
            <i className="fas fa-chart-bar"></i>
            Produtividade
          </button>
          <button
            className="btn btn-warning"
            onClick={() => navigate('/conferencia/pre-carregamento')}
            style={{ color: '#fff' }}
          >
            <i className="fas fa-box-open"></i>
            Pré-carregamento
          </button>
          <button
            className="btn"
            onClick={() => navigate('/conferencia/carregamento')}
            style={{ background: '#3498db', color: '#fff' }}
          >
            <i className="fas fa-truck-loading"></i>
            Carregamento
          </button>
          <button
            className="btn"
            onClick={() => navigate('/conferencia/descarregamento')}
            style={{ background: '#8e44ad', color: '#fff' }}
          >
            <i className="fas fa-arrow-down"></i>
            Descarregamento
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="conferencia-stats-grid">
          <div className="conferencia-stat-card">
            <div className="stat-header">
              <i className="fas fa-box-open" style={{ color: '#3498db' }}></i>
              <span>Total</span>
            </div>
            <p className="stat-value">{estatisticas.total}</p>
          </div>
          <div className="conferencia-stat-card">
            <div className="stat-header">
              <i className="fas fa-check-circle" style={{ color: '#27ae60' }}></i>
              <span>Concluídas</span>
            </div>
            <p className="stat-value" style={{ color: '#27ae60' }}>{estatisticas.concluidas}</p>
          </div>
          <div className="conferencia-stat-card">
            <div className="stat-header">
              <i className="fas fa-clock" style={{ color: '#f39c12' }}></i>
              <span>Pendentes</span>
            </div>
            <p className="stat-value" style={{ color: '#f39c12' }}>{estatisticas.pendentes}</p>
          </div>
          <div className="conferencia-stat-card">
            <div className="stat-header">
              <i className="fas fa-exclamation-triangle" style={{ color: '#e74c3c' }}></i>
              <span>Divergentes</span>
            </div>
            <p className="stat-value" style={{ color: '#e74c3c' }}>{estatisticas.divergentes}</p>
          </div>
          <div className="conferencia-stat-card">
            <div className="stat-header">
              <i className="fas fa-barcode" style={{ color: '#8e44ad' }}></i>
              <span>Itens Lidos</span>
            </div>
            <p className="stat-value" style={{ color: '#8e44ad' }}>{estatisticas.total_itens_lidos}</p>
          </div>
          <div className="conferencia-stat-card">
            <div className="stat-header">
              <i className="fas fa-exclamation-circle" style={{ color: '#e67e22' }}></i>
              <span>Divergências</span>
            </div>
            <p className="stat-value" style={{ color: '#e67e22' }}>{estatisticas.total_divergencias}</p>
          </div>
        </div>
      )}

      <div className="page-content">
        {/* Filtros */}
        <div className="conferencia-filters">
          <div className="filter-input">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar por nº palete ou código da etiqueta..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <option value="">Todos os tipos</option>
            <option value="pre_carregamento">Pré-Carregamento</option>
            <option value="carregamento">Carregamento</option>
            <option value="descarregamento">Descarregamento</option>
          </select>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="parcial">Parcial</option>
            <option value="concluida">Concluída</option>
            <option value="divergente">Divergente</option>
          </select>
          <select
            value={filtroCliente}
            onChange={(e) => setFiltroCliente(e.target.value)}
          >
            <option value="">Todos os clientes</option>
            {clientesOptions.map(c => (
              <option key={c.id} value={c.id}>{c.razao_social}</option>
            ))}
          </select>
          <input
            type="date"
            value={filtroDataInicio}
            onChange={(e) => { setFiltroDataInicio(e.target.value); setPage(1); }}
            title="Data inicial"
          />
          <input
            type="date"
            value={filtroDataFim}
            onChange={(e) => { setFiltroDataFim(e.target.value); setPage(1); }}
            title="Data final"
          />
          <input
            type="text"
            placeholder="Nº do pedido..."
            value={filtroNumeroPedido}
            onChange={(e) => { setFiltroNumeroPedido(e.target.value); setPage(1); }}
            title="Filtrar por número do pedido"
            style={{ minWidth: 160 }}
          />
        </div>

        {/* Tabela */}
        <div className="card">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
              <div className="loading-spinner-large"></div>
              <p>Carregando conferências...</p>
            </div>
          ) : conferencias.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
              <i className="fas fa-box-open" style={{ fontSize: '3rem', color: '#ddd', marginBottom: 16, display: 'block' }}></i>
              <p>Nenhuma conferência encontrada</p>
            </div>
          ) : (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Veículo</th>
                    <th>Itens</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {conferencias.map((conf) => (
                    <tr
                      key={conf.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/conferencia/${conf.id}`)}
                    >
                      <td style={{ fontWeight: 500 }}>#{conf.id}</td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {conf.tipo === 'pre_carregamento' && <i className="fas fa-box-open tipo-pre-carregamento"></i>}
                          {conf.tipo === 'carregamento' && <i className="fas fa-arrow-up tipo-carregamento"></i>}
                          {conf.tipo === 'descarregamento' && <i className="fas fa-arrow-down tipo-descarregamento"></i>}
                          {conf.tipo === 'pre_carregamento' ? 'Pré-Carregamento' : conf.tipo === 'carregamento' ? 'Carregamento' : 'Descarregamento'}
                        </span>
                      </td>
                      <td>{conf.cliente_razao_social || '-'}</td>
                      <td>
                        {conf.placa_veiculo ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <i className="fas fa-truck" style={{ color: '#7f8c8d' }}></i>
                            {conf.placa_veiculo}
                          </span>
                        ) : '-'}
                      </td>
                      <td>{conf.total_itens_conferido}/{conf.total_itens_esperado}</td>
                      <td>
                        <span className={`status-badge status-conferencia-${conf.status}`}>
                          {STATUS_LABELS[conf.status]}
                        </span>
                      </td>
                      <td>{new Date(conf.criado_em).toLocaleDateString('pt-BR')}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-buttons" onClick={(e) => e.stopPropagation()} style={{ justifyContent: 'flex-end' }}>
                          <button
                            className="btn-view"
                            onClick={() => navigate(`/conferencia/${conf.id}`)}
                            title="Visualizar"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn-view"
                            onClick={() => navigate(`/conferencia/${conf.id}/scanner`)}
                            title="Scanner"
                          >
                            <i className="fas fa-barcode"></i>
                          </button>
                          <button
                            className="btn-edit"
                            onClick={() => navigate(`/conferencia/${conf.id}/editar`)}
                            title="Editar"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn-delete"
                            onClick={async () => {
                              if (!window.confirm('Tem certeza que deseja excluir esta conferência?')) return;
                              try {
                                await deletarConferencia(conf.id);
                                carregarConferencias();
                              } catch (err) {
                                console.error('Erro ao excluir:', err);
                              }
                            }}
                            title="Excluir"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Anterior
              </button>
              <span style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>Página {page} de {totalPages}</span>
              <button
                className="pagination-btn"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConferenciaList;
